// The AI Employee's work on a conversation, as the ai_status socket event
// reports it: 'thinking' while it works on a reply (sent by ai-employee), then
// 'idle' or 'draft_ready' once the reply is sent, the draft is ready, or it
// stops without one (sent by message-consumer or ai-employee).

export interface AiStatusEvent {
  _customerConversation: string;
  status: string;
  mode?: string;
  // When the status changed (ISO). Older senders leave it out.
  at?: string;
}

// The AI has 90 seconds to finish a reply once it starts. A 'thinking' that
// nothing ends well after that lost its 'idle' (a dropped push, a restart).
export const AI_THINKING_TTL_MS = 120_000;

// Applies ai_status events in the order they happened. Two services send
// them, so one can arrive after a newer one: an event whose `at` is older than
// the last one applied for its conversation is dropped. A 'thinking' that
// nothing ends within the TTL is cleared to 'idle'.
export const createAiStatusListener = (
  apply: (event: AiStatusEvent) => void,
  ttlMs = AI_THINKING_TTL_MS,
) => {
  const latest = new Map<string, { at?: number; timer?: ReturnType<typeof setTimeout> }>();

  const listener = (event: AiStatusEvent) => {
    const id = event?._customerConversation ? String(event._customerConversation) : '';

    if (!id || !event.status) {
      return;
    }

    const at = Date.parse(event.at ?? '');
    const previous = latest.get(id);

    if (Number.isFinite(at) && previous?.at !== undefined && at < previous.at) {
      return;
    }

    if (previous?.timer) {
      clearTimeout(previous.timer);
    }

    const entry: { at?: number; timer?: ReturnType<typeof setTimeout> } = {
      at: Number.isFinite(at) ? at : previous?.at,
    };

    if (event.status === 'thinking') {
      entry.timer = setTimeout(() => {
        entry.timer = undefined;
        apply({ _customerConversation: id, status: 'idle' });
      }, ttlMs);
    }

    latest.set(id, entry);
    apply({ _customerConversation: id, status: event.status, mode: event.mode });
  };

  listener.dispose = () => {
    for (const entry of latest.values()) {
      if (entry.timer) {
        clearTimeout(entry.timer);
      }
    }

    latest.clear();
  };

  return listener;
};
